/**
 * Level JSON schema (SDD §5.1) + loader.
 * Coordinates are level-space pixels, +Y down.
 */

/** Polyline path used by moving platforms and moving anchors. */
export interface PathData {
  points: Array<{ x: number; y: number }>;
  /** px/sec along the polyline. */
  speed: number;
  loop: 'cycle' | 'pingpong';
  phase?: number;
}

export interface LevelData {
  id: string;
  name: string;
  spawn: { x: number; y: number };
  spawnVelocity?: { x: number; y: number };
  finish: { x: number; y: number; w: number; h: number };
  worldBounds: { minX: number; maxX: number; maxY: number };
  anchors: Array<{ x: number; y: number; hookRadius: number; path?: PathData }>;
  platforms: Array<{ x: number; y: number; w: number; h: number; angle?: number; bouncy?: boolean }>;
  hazards: Array<{ x: number; y: number; w: number; h: number }>;
  trampolines?: Array<{ x: number; y: number; w: number; h: number; power?: number; direction?: 'up' | 'down' | 'left' | 'right' }>;
  boosters?: Array<{ x: number; y: number; w: number; h: number; vx?: number; vy?: number }>;
  wind?: Array<{ x: number; y: number; w: number; h: number; fx?: number; fy?: number }>;
  /** Moving rectangular platforms that follow a polyline path. */
  movingPlatforms?: Array<{ x: number; y: number; w: number; h: number; angle?: number; bouncy?: boolean; path: PathData }>;
  /** Moving spike rectangles that follow a polyline path and kill on touch. */
  movingHazards?: Array<{ x: number; y: number; w: number; h: number; path: PathData }>;
  /** Sensor pads the player overlaps to drive gates. `id` links them to gates. */
  switches?: Array<{ x: number; y: number; w: number; h: number; id: string; mode?: 'toggle' | 'momentary' }>;
  /** Solid blocks that toggle on/off based on switches sharing the same `id`. */
  gates?: Array<{ x: number; y: number; w: number; h: number; angle?: number; id: string; inverted?: boolean }>;
  /** Sensor flags that override the respawn point on death. */
  checkpoints?: Array<{ x: number; y: number; vx?: number; vy?: number }>;
  /** Optional vertical gradient: top + bottom hex colors (e.g. "#4a1f6e"). */
  bgGradient?: { top: string; bottom: string };
}

const LEVEL_MODULES = import.meta.glob('../levels/*.json', { eager: true }) as Record<
  string,
  { default: LevelData }
>;

function parseLevelKey(path: string): string {
  const m = path.match(/([^/]+)\.json$/);
  return m ? m[1]! : path;
}

export function listLevels(): LevelData[] {
  // Numeric-aware sort so '102' lands after '99' instead of between '101'
  // and '11'. Falls back to plain string compare for non-numeric keys.
  return Object.entries(LEVEL_MODULES)
    .map(([path, mod]) => ({ key: parseLevelKey(path), data: mod.default }))
    .sort((a, b) => {
      const an = Number(a.key), bn = Number(b.key);
      const aNum = Number.isFinite(an), bNum = Number.isFinite(bn);
      if (aNum && bNum) return an - bn;
      if (aNum) return -1;
      if (bNum) return 1;
      return a.key.localeCompare(b.key);
    })
    .map((e) => e.data);
}

export function loadLevel(id: string): LevelData {
  const all = listLevels();
  const found = all.find((l) => l.id === id);
  if (!found) throw new Error(`Unknown level: ${id}`);
  return found;
}
